import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, formatDateTime, toNum } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { MoneyInput } from "@/components/MoneyInput";
import { toast } from "sonner";
import { ArrowLeftRight, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function Reconciliation() {
  const [apps, setApps] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ app_id: "", actual_balance: "", notes: "", adjust: true });

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/apps"), api.get("/reconciliation")])
      .then(([a, r]) => { setApps(a.data); setRows(r.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.app_id) return toast.error("Pilih aplikasi");
    await api.post("/reconciliation", { ...form, actual_balance: toNum(form.actual_balance) });
    toast.success("Rekonsiliasi tersimpan");
    setForm({ app_id: "", actual_balance: "", notes: "", adjust: true });
    load();
  };

  const selectedApp = apps.find((a) => a.id === form.app_id);
  const diff = selectedApp ? toNum(form.actual_balance) - Number(selectedApp.current_balance) : 0;

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Cek Selisih Saldo</p>
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Rekonsiliasi</h1>
      </div>

      <Card className="p-5">
        <p className="font-semibold flex items-center gap-2"><ArrowLeftRight className="h-4 w-4 text-primary" /> Rekonsiliasi Saldo Aplikasi</p>
        <p className="mt-1 text-xs text-muted-foreground">Bandingkan saldo sistem dengan saldo aktual di aplikasi Anda.</p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Aplikasi</Label>
            <Select value={form.app_id} onValueChange={(v) => setForm((f) => ({ ...f, app_id: v }))}>
              <SelectTrigger data-testid="recon-app"><SelectValue placeholder="Pilih aplikasi" /></SelectTrigger>
              <SelectContent>{apps.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · sistem {rupiah(a.current_balance)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Saldo Aktual</Label>
            <MoneyInput data-testid="recon-actual" value={form.actual_balance} onChange={(v) => setForm((f) => ({ ...f, actual_balance: v }))} />
          </div>
          <div className="md:col-span-2">
            <Label>Catatan</Label>
            <Textarea data-testid="recon-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-3 p-3 rounded-lg bg-accent/40">
            <div>
              <p className="text-sm font-medium">Terapkan penyesuaian otomatis</p>
              <p className="text-xs text-muted-foreground">Sesuaikan saldo sistem agar cocok dengan saldo aktual.</p>
            </div>
            <Switch data-testid="recon-adjust" checked={form.adjust} onCheckedChange={(v) => setForm((f) => ({ ...f, adjust: v }))} />
          </div>
        </div>

        {selectedApp && (
          <Card className={`mt-4 p-4 ${Math.abs(diff) > 0.01 ? "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-800" : "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-800"}`}>
            <div className="flex items-center gap-3">
              {Math.abs(diff) > 0.01 ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
              <div className="flex-1">
                <p className="text-sm font-semibold">
                  Selisih: <span className={`money ${diff === 0 ? "text-emerald-700" : diff > 0 ? "text-emerald-700" : "text-rose-700"}`}>{diff >= 0 ? "+" : ""}{rupiah(diff)}</span>
                </p>
                <p className="text-xs text-muted-foreground">
                  Sistem: {rupiah(selectedApp.current_balance)} | Aktual: {rupiah(form.actual_balance)}
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="mt-4">
          <Button data-testid="btn-save-recon" onClick={submit} disabled={!form.app_id} className="w-full">Simpan Rekonsiliasi</Button>
        </div>
      </Card>

      <div>
        <p className="text-sm font-semibold mb-2.5">Riwayat Rekonsiliasi</p>
        {loading ? <Skeleton className="h-40 w-full" /> : rows.length === 0 ? (
          <Card className="p-10 text-center text-sm text-muted-foreground">Belum ada riwayat</Card>
        ) : (
          <Card>
            <div className="divide-y">
              {rows.map((r) => (
                <div key={r.id} data-testid={`recon-row-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold">{r.app_name}</p>
                    <p className="text-xs text-muted-foreground">{formatDateTime(r.created_at)} · Sistem {rupiah(r.expected_balance)} → Aktual {rupiah(r.actual_balance)} {r.adjustment_applied ? "· disesuaikan" : ""}</p>
                    {r.notes && <p className="text-xs text-muted-foreground mt-0.5 italic">{`"${r.notes}"`}</p>}
                  </div>
                  <p className={`money font-semibold text-sm shrink-0 ${Math.abs(r.difference) < 0.01 ? "text-emerald-600" : r.difference > 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {r.difference >= 0 ? "+" : ""}{rupiah(r.difference)}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
